// START IMPORTS
import { Theme, useTheme } from '@ltvco/refresh-lib/theme';
import { Owner } from '@ltvco/refresh-lib/v1';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { Phone, Radar, Person2, TaskAlt, Message } from '@mui/icons-material';
// END IMPORTS

// START INTERFACES_AND_TYPES
type DataCounts = {
  potentialOwnersLength: number;
  searchVolumeLength: number;
  commentsLength: number;
  neighborSpoofingLength: number;
  spamReportsCount: number;
};
// END INTERFACES_AND_TYPES

// START HELPER_FUNCTIONS
export const getPhoneNavLinkData = (owner: Owner, dataCounts: DataCounts) => {
  const assetsCount =
    owner.ownedAssets['real_estate'].length +
    owner.ownedAssets['vehicles'].aircraft.length +
    owner.ownedAssets['vehicles'].watercraft.length +
    owner.ownedAssets['vehicles'].automobiles.length;
  const isPhonePartialAddress = useFeatureIsOn('OAR_1707');
  return [
    {
      href: '#phone-overview',
      sectionId: '#phone-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
    },
    {
      href: '#phone-fraudscan-section',
      sectionId: '#phone-fraudscan-section',
      text: 'FraudScan Summary',
      reportItemsCount: null,
      hideReportItemsCount: true,
    },
    {
      href: '#potential-owners-section',
      sectionId: '#potential-owners-section',
      text: 'Potential Owners',
      reportItemsCount: dataCounts.potentialOwnersLength,
      notificationSection: 'owners',
    },
    {
      href: '#possible-photos-section',
      sectionId: '#possible-photos-section',
      text: 'Possible Photos',
      reportItemsCount: owner.images?.length || null,
      notificationSection: 'photos',
    },
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Other Phone Numbers',
      reportItemsCount: Math.max(owner.phones.length - 1, 0),
      notificationSection: 'phones',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Email Addresses',
      reportItemsCount: owner.emails.length,
      notificationSection: 'emails',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: isPhonePartialAddress ? 'Possible Locations' : 'Address History',
      reportItemsCount: owner.addresses.length,
      notificationSection: 'addresses',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Work History',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      text: 'Educational Background',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social Media',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'socials',
    },
    {
      href: '#neighbor-spoofing-section',
      sectionId: '#neighbor-spoofing-section',
      text: 'Neighbor Spoofing',
      reportItemsCount: dataCounts.neighborSpoofingLength,
      notificationSection: 'neighbors',
    },
    {
      href: '#search-volume-section',
      sectionId: '#search-volume-section',
      text: 'Search Volume',
      reportItemsCount: dataCounts.searchVolumeLength,
    },
    {
      href: '#nuisance-caller-section',
      sectionId: '#nuisance-caller-section',
      text: 'Nuisance Caller',
      reportItemsCount: dataCounts.spamReportsCount,
    },
    {
      href: '#comments-section',
      sectionId: '#comments-section',
      text: 'Comments',
      reportItemsCount: dataCounts.commentsLength,
    },
    {
      href: '#possible-owned-assets-section',
      sectionId: '#possible-owned-assets-section',
      text: 'Assets',
      reportItemsCount: assetsCount,
      notificationSection: 'real_estate',
    },
  ];
};

export const getPhoneNavLinkDataV2 = (
  owner: Owner,
  dataCounts: DataCounts,
  theme: Theme
) => {
  const assetsCount =
    owner.ownedAssets['real_estate'].length +
    owner.ownedAssets['vehicles'].aircraft.length +
    owner.ownedAssets['vehicles'].watercraft.length +
    owner.ownedAssets['vehicles'].automobiles.length;

  const isPhonePartialAddress = useFeatureIsOn('OAR_1707');

  const PotentialOwnerChildItems = [
    {
      href: '#potential-owners-section',
      labelText: `Potential ${
        dataCounts.potentialOwnersLength > 1 ? 'Owners' : 'Owner'
      }`,
      reportItemsCount: dataCounts.potentialOwnersLength,
      notificationSection: 'owners',
      name: 'potential-owners-section',
    },
    {
      href: '#phone-numbers-section',
      labelText: 'Phone Numbers',
      reportItemsCount: Math.max(owner.phones.length - 1, 0),
      notificationSection: 'phones',
      name: 'phone-numbers-section',
    },
    {
      href: '#email-section',
      labelText: 'Email Addresses',
      reportItemsCount: owner.emails.length,
      notificationSection: 'emails',
      name: 'email-section',
    },
    {
      href: '#address-history-section',
      labelText: isPhonePartialAddress
        ? 'Possible Locations'
        : 'Address History',
      reportItemsCount: owner.addresses.length,
      notificationSection: 'addresses',
      name: 'address-history-section',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      labelText: 'Social Media',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'social',
      name: 'social-media-section',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      labelText: 'Jobs',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
      name: 'jobs-section',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      labelText: 'Education',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
      name: 'education-section',
    },
    {
      labelText: 'Assets',
      href: '#possible-owned-assets-section',
      reportItemsCount: assetsCount,
      notificationSection: 'real_estate',
      name: 'assets-section',
    },
  ];

  return [
    {
      href: '#phone-overview',
      labelText: 'Overview',
      icons: [
        <Phone
          sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
          key="phone-overview"
        />,
      ],
      name: 'phone-overview',
    },
    {
      labelText: 'Potential Owners',
      icons: [
        <Person2
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
          key="potential-owners"
        />,
      ],
      name: 'potential-owners',
      childItems: PotentialOwnerChildItems,
    },
    {
      labelText: 'Threat Detection',
      href: '#threat-detection-section',
      icons: [
        <Radar
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
          key="threat-detection-section"
        />,
      ],
      name: 'threat-detection-section',
      hideReportItemsCount: true,
    },
    {
      href: '#comments-section',
      sectionId: '#comments-section',
      labelText: 'Comments',
      icons: [<Message sx={{ color: theme.palette.primary.main }} />],
      reportItemsCount: dataCounts.commentsLength,
    },
    {
      labelText: 'Next Steps',
      icons: [
        <TaskAlt
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
          key="next-steps"
        />,
      ],
      name: 'next-steps',
      childItems: [
        {
          href: '#attachments-section',
          labelText: 'Report Attachments',
          hideReportItemsCount: true,
          name: 'attachments',
          visible: false,
        },
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
          name: 'notes-section',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports-section',
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
          name: 'report-rating',
        },
      ],
    },
  ];
};

export const getPhoneNavLinkDataReorderedPhoneReportVariation = (
  owner: Owner,
  dataCounts: DataCounts
) => {
  const theme = useTheme();
  const assetsCount =
    owner.ownedAssets['real_estate'].length +
    owner.ownedAssets['vehicles'].aircraft.length +
    owner.ownedAssets['vehicles'].watercraft.length +
    owner.ownedAssets['vehicles'].automobiles.length;
  return [
    {
      href: '#phone-overview',
      labelText: 'Overview',
      icons: [
        <Phone
          sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
        />,
      ],
      name: 'phone-overview',
    },
    {
      labelText: 'Potential Owners',
      icons: [
        <Person2
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      name: 'potential-owners',
      childItems: [
        {
          href: '#potential-owners-section',
          labelText: 'Potential Owners',
          reportItemsCount: dataCounts.potentialOwnersLength,
          name: 'potential-owners-section',
        },
        {
          href: '#possible-photos-section',
          labelText: 'Possible Photos',
          reportItemsCount: owner.images?.length || null,
          name: 'possible-photos-section',
        },
        {
          href: '#phone-numbers-section',
          labelText: 'Phone Numbers',
          reportItemsCount: Math.max(owner.phones.length - 1, 0),
          name: 'phone-numbers-section',
        },
        {
          href: '#email-section',
          labelText: 'Email Addresses',
          reportItemsCount: owner.emails.length,
          name: 'email-section',
        },
        {
          href: '#address-history-section',
          labelText: 'Address History',
          reportItemsCount: owner.addresses.length,
          name: 'address-history-section',
        },
        {
          href: '#social-media-section',
          sectionId: '#social-media-section',
          labelText: 'Social Media',
          reportItemsCount: owner.profiles.length + owner.usernames.length,
          name: 'social-media-section',
        },
        {
          href: '#jobs-section',
          sectionId: '#jobs-section',
          labelText: 'Jobs',
          reportItemsCount: owner.jobs.length,
          name: 'jobs-section',
        },
        {
          href: '#education-section',
          sectionId: '#education-section',
          labelText: 'Education',
          reportItemsCount: owner.educations.length,
          name: 'education-section',
        },
        {
          labelText: 'Assets',
          href: '#possible-owned-assets-section',
          reportItemsCount: assetsCount,
          name: 'assets-section',
        },
      ],
    },
    {
      labelText: 'FraudScan',
      icons: [
        <Radar
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      name: 'fraudscan',
      childItems: [
        {
          href: '#phone-fraudscan-section',
          labelText: 'Summary',
          hideReportItemsCount: true,
          name: 'phone-fraudscan-section',
        },
        {
          href: '#neighbor-spoofing-section',
          labelText: 'Neighbor Spoofing',
          reportItemsCount: dataCounts.neighborSpoofingLength,
          name: 'neighbor-spoofing-section',
        },
        {
          href: '#search-volume-section',
          labelText: 'Search Volume',
          reportItemsCount: dataCounts.searchVolumeLength,
          name: 'search-volume-section',
        },
        {
          href: '#nuisance-caller-section',
          labelText: 'Nuisance Caller',
          reportItemsCount: dataCounts.spamReportsCount,
          name: 'nuisance-caller-section',
        },
        {
          href: '#comments-section',
          labelText: 'Comments',
          reportItemsCount: dataCounts.commentsLength,
          name: 'comments-section',
        },
      ],
    },

    {
      labelText: 'Next Steps',
      icons: [
        <TaskAlt
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      name: 'next-steps',
      childItems: [
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
          name: 'notes-section',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports-section',
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
          name: 'report-rating',
        },
      ],
    },
  ];
};
// END HELPER_FUNCTIONS
